import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, 
  Lightbulb, 
  FileText, 
  Loader2,
  Sparkles,
  TrendingUp,
  Target,
  DollarSign,
  Users,
  Rocket
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BusinessBuilderPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('idea');
  const [loading, setLoading] = useState(false);
  
  // Idea generator form
  const [ideaForm, setIdeaForm] = useState({
    industry: '',
    budget: '',
    interests: '',
    experience: ''
  });
  const [generatedIdea, setGeneratedIdea] = useState(null);
  
  // Idea to Reality form
  const [ideaInput, setIdeaInput] = useState('');
  const [blueprint, setBlueprint] = useState(null);
  
  // Business Plan form
  const [planForm, setPlanForm] = useState({
    idea_title: '',
    idea_summary: ''
  });
  const [businessPlan, setBusinessPlan] = useState('');

  const generateIdea = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/growth/business/idea`, {
        ...ideaForm,
        interests: ideaForm.interests.split(',').map(i => i.trim()).filter(Boolean)
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setGeneratedIdea(response.data.idea);
      toast.success('Business idea generated!');
    } catch (error) {
      toast.error('Failed to generate idea');
    } finally {
      setLoading(false);
    }
  };

  const transformIdea = async () => {
    if (!ideaInput.trim()) {
      toast.error('Please enter an idea');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/growth/idea-to-reality`, {
        idea: ideaInput
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setBlueprint(response.data.blueprint);
      toast.success('Blueprint generated!');
    } catch (error) {
      toast.error('Failed to transform idea');
    } finally {
      setLoading(false);
    }
  };

  const generatePlan = async () => {
    if (!planForm.idea_title || !planForm.idea_summary) {
      toast.error('Please fill in the idea details');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/growth/business/plan`, planForm, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setBusinessPlan(response.data.plan);
      toast.success('Business plan generated!');
    } catch (error) {
      toast.error('Failed to generate plan');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'idea', label: 'Idea Generator', icon: Lightbulb },
    { id: 'reality', label: 'Idea to Reality', icon: Rocket },
    { id: 'plan', label: 'Business Plan', icon: FileText },
  ];

  return (
    <div className="max-w-6xl mx-auto" data-testid="business-builder-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">AIRA Business Builder</h1>
        <p className="text-gray-400">Transform ideas into successful ventures</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8 justify-center">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-primary to-secondary text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid={`${tab.id}-tab`}
          >
            <tab.icon className="w-5 h-5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Idea Generator */}
      {activeTab === 'idea' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <GlassCard className="p-6">
            <h3 className="font-heading text-xl font-semibold mb-6 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              Generate Business Idea
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Industry Interest</label>
                <input
                  type="text"
                  value={ideaForm.industry}
                  onChange={(e) => setIdeaForm(prev => ({ ...prev, industry: e.target.value }))}
                  className="input-field w-full"
                  placeholder="e.g., EdTech, FinTech, Healthcare"
                  data-testid="idea-industry-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Budget Range</label>
                <select
                  value={ideaForm.budget}
                  onChange={(e) => setIdeaForm(prev => ({ ...prev, budget: e.target.value }))}
                  className="input-field w-full"
                  data-testid="idea-budget-select"
                >
                  <option value="">Select budget</option>
                  <option value="Under ₹1 Lakh">Under ₹1 Lakh</option>
                  <option value="₹1-5 Lakhs">₹1-5 Lakhs</option>
                  <option value="₹5-15 Lakhs">₹5-15 Lakhs</option>
                  <option value="₹15-50 Lakhs">₹15-50 Lakhs</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Your Interests (comma separated)</label>
                <input
                  type="text"
                  value={ideaForm.interests}
                  onChange={(e) => setIdeaForm(prev => ({ ...prev, interests: e.target.value }))}
                  className="input-field w-full"
                  placeholder="e.g., technology, sustainability, education"
                  data-testid="idea-interests-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Experience Level</label>
                <select
                  value={ideaForm.experience}
                  onChange={(e) => setIdeaForm(prev => ({ ...prev, experience: e.target.value }))}
                  className="input-field w-full"
                  data-testid="idea-experience-select"
                >
                  <option value="">Select experience</option>
                  <option value="First-time entrepreneur">First-time entrepreneur</option>
                  <option value="Some experience">Some experience</option>
                  <option value="Serial entrepreneur">Serial entrepreneur</option>
                </select>
              </div>

              <button
                onClick={generateIdea}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="generate-idea-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                Generate Idea
              </button>
            </div>
          </GlassCard>

          <AnimatePresence mode="wait">
            {generatedIdea ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <GlassCard className="p-6 h-full">
                  <h3 className="font-heading text-2xl font-bold mb-2 text-primary">{generatedIdea.idea_title}</h3>
                  <p className="text-gray-300 mb-6">{generatedIdea.idea_summary}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-white/5 rounded-xl">
                      <DollarSign className="w-5 h-5 text-green-400 mb-2" />
                      <p className="text-sm text-gray-400">Investment</p>
                      <p className="font-semibold">{generatedIdea.estimated_investment}</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-xl">
                      <Users className="w-5 h-5 text-blue-400 mb-2" />
                      <p className="text-sm text-gray-400">Target Market</p>
                      <p className="font-semibold">{generatedIdea.target_market}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm text-gray-500 mb-2">Revenue Model</h4>
                    <p className="text-sm">{generatedIdea.revenue_model}</p>
                  </div>

                  <div>
                    <h4 className="text-sm text-gray-500 mb-2">Growth Opportunities</h4>
                    <div className="space-y-2">
                      {generatedIdea.growth_opportunities?.map((g, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-green-400" />
                          <span className="text-sm">{g}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ) : (
              <GlassCard className="p-12 flex items-center justify-center h-full">
                <div className="text-center">
                  <Lightbulb className="w-16 h-16 text-primary/30 mx-auto mb-4" />
                  <p className="text-gray-500">Your AI-generated business idea will appear here</p>
                </div>
              </GlassCard>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Idea to Reality */}
      {activeTab === 'reality' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <GlassCard className="p-6">
            <h3 className="font-heading text-xl font-semibold mb-6 flex items-center gap-2">
              <Rocket className="w-5 h-5 text-primary" />
              Transform Your Idea
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Your Idea</label>
                <textarea
                  value={ideaInput}
                  onChange={(e) => setIdeaInput(e.target.value)}
                  className="input-field w-full h-40 resize-none"
                  placeholder="Describe your business idea in detail..."
                  data-testid="idea-input"
                />
              </div>

              <button
                onClick={transformIdea}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="transform-idea-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
                Transform to Blueprint
              </button>
            </div>
          </GlassCard>

          <AnimatePresence mode="wait">
            {blueprint ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <GlassCard className="p-6 max-h-[600px] overflow-y-auto custom-scrollbar">
                  <h3 className="font-heading text-2xl font-bold mb-2">{blueprint.business_name}</h3>
                  <p className="text-primary italic mb-4">"{blueprint.tagline}"</p>
                  
                  <div className="p-4 bg-primary/10 rounded-xl mb-6">
                    <p className="text-sm text-gray-400">Value Proposition</p>
                    <p className="font-medium">{blueprint.value_proposition}</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm text-gray-500 mb-2">Revenue Streams</h4>
                      <div className="flex flex-wrap gap-2">
                        {blueprint.revenue_streams?.map((r, i) => (
                          <span key={i} className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">{r}</span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm text-gray-500 mb-2">Launch Roadmap</h4>
                      <div className="space-y-2">
                        {blueprint.launch_roadmap?.map((step, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-sm text-primary flex-shrink-0 mt-0.5">{i + 1}</div>
                            <span className="text-sm">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm text-gray-500 mb-2">Week 1 Actions</h4>
                      <div className="space-y-1">
                        {blueprint.first_week_actions?.map((action, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-accent" />
                            <span className="text-sm">{action}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ) : (
              <GlassCard className="p-12 flex items-center justify-center h-full">
                <div className="text-center">
                  <Rocket className="w-16 h-16 text-primary/30 mx-auto mb-4" />
                  <p className="text-gray-500">Your actionable blueprint will appear here</p>
                </div>
              </GlassCard>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Business Plan */}
      {activeTab === 'plan' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <GlassCard className="p-6">
            <h3 className="font-heading text-xl font-semibold mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Generate Business Plan
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Business/Idea Title</label>
                <input
                  type="text"
                  value={planForm.idea_title}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, idea_title: e.target.value }))}
                  className="input-field w-full"
                  placeholder="e.g., EduAI - AI Tutoring Platform"
                  data-testid="plan-title-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Idea Summary</label>
                <textarea
                  value={planForm.idea_summary}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, idea_summary: e.target.value }))}
                  className="input-field w-full h-32 resize-none"
                  placeholder="Describe what your business does..."
                  data-testid="plan-summary-input"
                />
              </div>

              <button
                onClick={generatePlan}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="generate-plan-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                Generate Full Plan
              </button>
            </div>
          </GlassCard>

          <GlassCard className="p-6 max-h-[600px] overflow-y-auto custom-scrollbar">
            {businessPlan ? (
              <div className="prose prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-gray-300 leading-relaxed text-sm">
                  {businessPlan}
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-primary/30 mx-auto mb-4" />
                  <p className="text-gray-500">Your comprehensive business plan will appear here</p>
                </div>
              </div>
            )}
          </GlassCard>
        </div>
      )}
    </div>
  );
}
